import React, { useEffect, useState } from "react";
import { callFunction } from "../api/callFunction";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../components/Button";
import { formatSentAt } from "../Events/outreachFormat";

// The two dialogs behind the Host nudges table: what a host would receive, and
// everything they have received so far.

const KIND_LABELS = {
  repeat: "Repeat",
  suggestion: "From the idea feed",
  new: "New idea",
};

const ideaWhen = (idea) => {
  const ms = idea?.dateMs;
  if (!Number.isFinite(ms)) return idea?.date || "";
  return new Date(ms).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const Shell = ({ title, subtitle, children, footer, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true">
    <div className="w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
      <div className="mb-4">
        <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>
        {subtitle ? <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div> : null}
      </div>
      {children}
      <div className="mt-5 flex justify-end gap-2">
        {footer}
        <SecondaryButtonSmall onClick={onClose}>Close</SecondaryButtonSmall>
      </div>
    </div>
  </div>
);

/**
 * What this host would get. The text is the real one: the preview drafts their
 * ideas and saves them, and the send that follows reuses the same plan, so the
 * words here are the words that go out.
 */
export function NudgePreviewDialog({ row, preview, sending, onSend, onClose }) {
  const text = preview?.text || "";
  const ideas = preview?.plan?.ideas || [];
  return (
    <Shell
      title={`What ${row?.name || "this host"} would get`}
      subtitle="Nothing has been sent. Their ideas on the panel have been drafted."
      onClose={onClose}
      footer={
        <PrimaryButtonSmall onClick={onSend} loading={sending} loadingLabel="Sending" disabled={sending}>
          Send this now
        </PrimaryButtonSmall>
      }
    >
      <div className="rounded-lg bg-gray-100 dark:bg-gray-800 p-3 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line break-words">
        {text}
      </div>
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {text.length} characters, {text.length > 160 ? "2 segments" : "1 segment"}
      </div>

      <div className="mt-4">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {ideas.length === 0 ? "No ideas drafted" : `${ideas.length} idea${ideas.length === 1 ? "" : "s"} on their panel`}
        </div>
        <ul className="mt-2 space-y-2">
          {ideas.map((idea) => (
            <li key={idea.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{idea.title}</div>
                <span className="shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                  {KIND_LABELS[idea.kind] || idea.kind}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {ideaWhen(idea)}{idea.venue ? ` at ${idea.venue}` : ""}
              </div>
              {idea.blurb ? <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">{idea.blurb}</div> : null}
              {idea.why ? <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{idea.why}</div> : null}
            </li>
          ))}
        </ul>
        {preview?.plan?.source === "fallback" && ideas.length > 0 ? (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Drafted from their past events. The idea writer did not answer this time.
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

/** Every nudge this host has been sent, newest first, with the words used. */
export function HostNudgeHistoryDialog({ row, onClose }) {
  const [messages, setMessages] = useState(null); // null until loaded
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await callFunction("adminGetHostCadence", { vendorID: row.vendorID }, { timeout: 60000 });
        if (!cancelled) setMessages(Array.isArray(data?.messages) ? data.messages : []);
      } catch (err) {
        console.error("adminGetHostCadence failed", err);
        if (!cancelled) {
          setError(err?.message || "Could not load their history.");
          setMessages([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [row.vendorID]);

  return (
    <Shell title={row?.name || "This host"} subtitle="Every nudge sent to them" onClose={onClose}>
      {messages === null ? <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div> : null}
      {error ? <div className="text-sm text-red-600 dark:text-red-400">{error}</div> : null}
      {messages && messages.length === 0 && !error ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Nothing has been sent to them yet.</div>
      ) : null}
      <ul className="space-y-3">
        {(messages || []).map((m) => (
          <li key={m.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{formatSentAt(m.sentAt)}</span>
              <span>{m.automatic ? "Automatic" : m.byName || "An operator"}</span>
            </div>
            <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line break-words">{m.text}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {m.reached ? `Delivered, ${m.ideaCount} idea${m.ideaCount === 1 ? "" : "s"}` : `Not delivered${m.error ? `: ${m.error}` : ""}`}
            </div>
          </li>
        ))}
      </ul>
    </Shell>
  );
}
